// Rare water puddles: passable hazards. Standing in one tops up a "wet" timer that
// keeps the entity slowed for WET.durationSec after it leaves. Seeded per run, looked up
// via a static spatial hash (same pattern as boulders).

import { SpatialHash } from '../core/spatialHash';
import { Rng } from '../core/rng';
import { ALTAR, ARENA, PUDDLE } from './balance';

export interface Puddle {
  x: number;
  y: number;
  radius: number;
  shape: number; // which sprite variant to draw
}

export function generatePuddles(seed: number): Puddle[] {
  const rng = new Rng((seed ^ 0x85ebca6b) >>> 0);
  const out: Puddle[] = [];
  const cx = ARENA.width / 2;
  const cy = ARENA.height / 2;
  let attempts = 0;
  while (out.length < PUDDLE.count && attempts < PUDDLE.count * 25) {
    attempts++;
    const radius = rng.range(PUDDLE.radiusMin, PUDDLE.radiusMax);
    const x = rng.range(radius, ARENA.width - radius);
    const y = rng.range(radius, ARENA.height - radius);
    if (Math.hypot(x - cx, y - cy) < PUDDLE.clearCenterPx) continue;
    if (Math.hypot(x - ALTAR.x, y - ALTAR.y) < ALTAR.radius + radius + 40) continue; // keep the altar clear
    let ok = true;
    for (const p of out) {
      if (Math.hypot(x - p.x, y - p.y) < p.radius + radius + PUDDLE.minSpacingPx) {
        ok = false;
        break;
      }
    }
    if (ok) out.push({ x, y, radius, shape: rng.int(0, PUDDLE.shapes) });
  }
  return out;
}

export function buildPuddleHash(puddles: Puddle[], cellSize: number): SpatialHash {
  const hash = new SpatialHash(cellSize);
  for (let i = 0; i < puddles.length; i++) hash.insert(i, puddles[i].x, puddles[i].y);
  return hash;
}

/** True if (x,y) is standing in a puddle. */
export function inPuddle(
  x: number,
  y: number,
  puddles: Puddle[],
  hash: SpatialHash,
  buf: number[],
): boolean {
  const n = hash.query(x, y, PUDDLE.maxRadius, buf);
  for (let i = 0; i < n; i++) {
    const p = puddles[buf[i]];
    const dx = x - p.x;
    const dy = y - p.y;
    if (dx * dx + dy * dy <= p.radius * p.radius) return true;
  }
  return false;
}
